import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format number with locale string (always whole numbers)
 */
export function formatNumber(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Safe number parsing with default value
 */
export function safeNumber(value: unknown, defaultValue = 0): number {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Safe math operation
 */
export function safeMath(
  value: number,
  operation: "round" | "floor" | "ceil" = "round"
): number {
  if (!isFinite(value)) return 0;
  return Math[operation](value);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Sleep function for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Format line value from "line_1" to "{prefix} 1"
 * @param lineValue - Raw line value like "line_1"
 * @param linePrefix - Terminology prefix like "Skid" or "Line"
 */
export function formatLine(lineValue: string, linePrefix: string): string {
  if (!lineValue) return "-";
  const parts = lineValue.split("_");
  let lineNumber = lineValue; // fallback to original
  for (let i = 0; i < parts.length - 1; i++) {
    if (parts[i].toLowerCase().includes("line")) {
      lineNumber = parts[i + 1];
      break;
    }
  }
  return `${linePrefix} ${lineNumber}`;
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(str: string): string {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Column width constants for tables
 */
export const COLUMN_WIDTHS = {
  DEFAULT: "120px",
  NARROW: "80px", // Used for columns like health, alerts, line/skid
} as const;
